import { FormLabel, IconButton } from '@material-ui/core';
import Switch from '@material-ui/core/Switch';
import { Close } from '@material-ui/icons';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useCreateReconVelocityPlugin,
  useReconVelocityPlugin,
  useUpdateReconVelocityPlugin,
} from 'api/organizations/plugins/reconvelocity';
import { useOnChange } from 'common/hooks';
import permissions from 'common/permissions';
import Dialog from 'components/shared/Dialog';
import LoadingIndicator from 'components/shared/LoadingIndicator';
import Tabs, { InlineTab, TabHeaderItem } from 'components/shared/Tabs';
import { useSendInventoryUpdates } from 'hooks/usePubSubUpdates';
import {
  Organization,
  PluginItem,
  PluginItemCategory,
  PluginItemSubCategory,
} from 'models';

import GeneralView from './GeneralView';
import IngestProcessView from './IngestProcessView';
import TagDefinitionsView from './TagDefinitionsView';
import TasksView from './TasksView';
import WorkflowDetailView from './WorkflowsView/WorkflowDetailView';

import './ReconVelocityPluginDialog.scss';

const defaultReconVelocityPlugin: PluginItem = {
  enabled: true,
  category: PluginItemCategory.MODULE,
  subCategory: PluginItemSubCategory.NOT_SET,
  title: 'Recon Velocity',
  pluginName: 'RECON_VELOCITY',
  settings: {
    // TODO - and another place for default settings for plugins. Refactor needed
    RECON_TIME_IGNORE_SCHEDULE: {
      dataType: 'BOOL',
      value: true,
    },
  },
  description: '',
  iconUrl: '',
  links: null,
};

interface ReconVelocityPluginDialogProps {
  orgId: Organization['id'];
  onClose?: () => void;
  isNewPlugin: boolean;
  organization: Organization;
}

const rvPluginTabs: TabHeaderItem[] = [
  { key: 'general', label: 'General' },
  {
    key: 'tags',
    label: 'Tags',
    permission: permissions.ORGS_PLUGINS_RECONVELOCITY_TAG_DEFINITIONS_VIEW,
  },
  {
    key: 'tasks',
    label: 'Tasks',
    permission: permissions.ORGS_PLUGINS_RECONVELOCITY_TASK_DEFINITIONS_VIEW,
  },
  {
    key: 'workflows',
    label: 'Steps',
    permission: permissions.ORGS_PLUGINS_RECONVELOCITY_WORKFLOWS_VIEW,
  },
  {
    key: 'startReconStep',
    label: 'Start Recon Step',
    permission: permissions.ORGS_PLUGINS_RECONVELOCITY_VIEW,
  },
];

const ReconVelocityPluginDialog = ({
  orgId,
  onClose,
  isNewPlugin,
  organization,
}: ReconVelocityPluginDialogProps) => {
  const {
    data: generalSettings,
    isLoading: generalSettingsAreLoading,
    refetch: reloadGeneralSettings,
  } = useReconVelocityPlugin(orgId, {
    enabled: !isNewPlugin,
  });
  const { updateReconVelocityPluginAsync, isLoading: isUpdating } =
    useUpdateReconVelocityPlugin(orgId);
  const { createReconVelocityPluginAsync, isLoading: isCreating } =
    useCreateReconVelocityPlugin(orgId);
  const navigate = useNavigate();

  const defaultTabKey = 'general';
  const baseRoute = 'reconvelocity/';

  const onMountNavigate = useCallback(() => {
    navigate(baseRoute + defaultTabKey, { replace: true });
  }, [navigate]);

  useOnChange(onMountNavigate, null, { callOnMount: true });

  // if new plugin, we'll assume they want it enabled
  // so we need this funky logic
  const generalSettingsData = generalSettings?.data;
  const onMountSetupPlugin = useCallback(() => {
    const createAndFetchNewPlugin = async () => {
      // TODO - none of these should be done in the UI
      const plugin = {
        ...defaultReconVelocityPlugin,
        ...generalSettingsData,
        reconTimeIgnoreSchedule: true,
        enabled: true,
      };
      // This call creates the plugin with a POST request.
      await createReconVelocityPluginAsync(plugin);

      // This call enables the plugin, since POST does not seem to allow it.
      await updateReconVelocityPluginAsync(plugin);
      await reloadGeneralSettings(); // TODO - there are too many calls for this plugin
    };
    if (isNewPlugin) {
      createAndFetchNewPlugin();
    }
  }, [
    createReconVelocityPluginAsync,
    generalSettingsData,
    isNewPlugin,
    reloadGeneralSettings,
    updateReconVelocityPluginAsync,
  ]);

  useOnChange(onMountSetupPlugin, null, { callOnMount: true });
  useSendInventoryUpdates(orgId);

  const onChange = async (event: React.ChangeEvent, checked: boolean) => {
    await updateReconVelocityPluginAsync({
      ...defaultReconVelocityPlugin,
      ...generalSettings?.data,
      enabled: checked,
    });
  };

  const InlineTabsCollection = [
    {
      element: (
        <GeneralView
          orgId={orgId}
          fetchOnMount={!isNewPlugin}
          isCreatingPlugin={isCreating || isUpdating}
        />
      ),
      path: rvPluginTabs[0].key,
    },
    {
      element: <TagDefinitionsView orgId={orgId} fetchOnMount={!isNewPlugin} />,
      path: rvPluginTabs[1].key,
    },
    {
      element: <TasksView orgId={orgId} fetchOnMount={!isNewPlugin} />,
      path: rvPluginTabs[2].key,
    },
    {
      element: <WorkflowDetailView orgId={orgId} organization={organization} />,
      path: rvPluginTabs[3].key,
    },
    {
      element: <IngestProcessView orgId={orgId} fetchOnMount={!isNewPlugin} />,
      path: rvPluginTabs[4].key,
    },
  ].map(({ element, path }) => (
    <InlineTab
      key={path}
      path={path}
      className="ReconVelocityPluginDialog__body__tabContent"
      element={element}
    />
  ));

  return (
    <Dialog
      open
      title="ReconVelocity"
      className="ReconVelocityPluginDialog"
      maxWidth="lg"
      fullWidth
      height={740}
      showCloseButton
      hidePrimaryButton
      hideCancelButton
      noPadding
      closeElement={
        <div className="ReconVelocityPluginDialog__closeElement">
          <FormLabel id="switch-label">
            {generalSettings?.data?.enabled ? 'Enabled' : 'Disabled'}
          </FormLabel>
          <Switch
            id="plugin-toggle-button"
            checked={generalSettings?.data?.enabled ?? false}
            disabled={generalSettingsAreLoading}
            onChange={onChange}
          />
          <IconButton id="close-modal-icon-button" onClick={onClose}>
            <Close />
          </IconButton>
        </div>
      }
      block={generalSettingsAreLoading}
    >
      {generalSettingsAreLoading ? (
        <LoadingIndicator />
      ) : (
        <div className="ReconVelocityPluginDialog__body">
          <Tabs
            tabHeaderItems={rvPluginTabs}
            defaultTabKey={defaultTabKey}
            basename={baseRoute}
          />
          {InlineTabsCollection}
        </div>
      )}
    </Dialog>
  );
};

export default ReconVelocityPluginDialog;
